import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { ContactForm } from '../../components/forms/ContactForm'
import { Avatar } from '../../components/ui/Avatar'
import { Pagination } from '../../components/ui/Pagination'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import type { Contact, ContactInput } from '../../../shared/types'

const contactsApi = createCrudApi<Contact, ContactInput>('contacts')

type ContactFilter = 'all' | 'with_email' | 'with_phone'
type SortField = 'name' | 'email' | 'phone' | 'title'
type SortDirection = 'asc' | 'desc'

export function ContactsListPage() {
  const [showForm, setShowForm] = useState(false)
  const [contactFilter, setContactFilter] = useState<ContactFilter>('all')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { getUser } = useUserLookup()

  // Build filters object for API
  const filters: Record<string, string> | undefined = contactFilter === 'with_email'
    ? { hasEmail: 'true' }
    : contactFilter === 'with_phone'
      ? { hasPhone: 'true' }
      : undefined

  const { data: response, isLoading } = useQuery({
    queryKey: ['contacts', { page, limit, filters }],
    queryFn: () => contactsApi.list({ page, limit, filters }),
  })

  const contacts = response?.data

  // Handle filter change - reset to page 1
  const handleContactFilter = (filter: ContactFilter) => {
    setContactFilter(filter)
    setPage(1)
  }

  const createMutation = useMutation({
    mutationFn: contactsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setShowForm(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: contactsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Client-side sorting only (filtering is now server-side)
  const sortedContacts = useMemo(() => {
    if (!contacts) return []

    const result = [...contacts]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'name':
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
          comparison = nameA.localeCompare(nameB)
          break
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '')
          break
        case 'phone':
          comparison = (a.phone || '').localeCompare(b.phone || '')
          break
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '')
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [contacts, sortField, sortDirection])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  if (isLoading) return <div>{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.contacts.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.contacts.description')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('pages.contacts.addButton')}
          </button>

          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.contacts.newTitle')}</h3>
              <ContactForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
              />
            </div>
          )}

          {/* Contact Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleContactFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  contactFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleContactFilter('with_email')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  contactFilter === 'with_email'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('pages.contacts.stats.withEmail')}
              </button>
              <button
                onClick={() => handleContactFilter('with_phone')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  contactFilter === 'with_phone'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('pages.contacts.stats.withPhone')}
              </button>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.contacts.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.contacts.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pagination.page')}</dt>
                <dd className="font-medium text-gray-900">{response?.page || 1} {t('pagination.of')} {response?.totalPages || 1}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.contacts.title')}</h1>
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              {showForm ? t('common.cancel') : t('pages.contacts.addButton')}
            </button>
            {showForm && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.contacts.newTitle')}</h2>
                <ContactForm
                  onSubmit={(data) => createMutation.mutate(data)}
                  onCancel={() => setShowForm(false)}
                  loading={createMutation.isPending}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader field="name">
                          <span className="pl-4 sm:pl-6">{t('fields.name')}</span>
                        </SortableHeader>
                        <SortableHeader field="email">
                          {t('fields.email')}
                        </SortableHeader>
                        <SortableHeader field="phone">
                          {t('fields.phone')}
                        </SortableHeader>
                        <SortableHeader field="title">
                          {t('fields.title')}
                        </SortableHeader>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.owner')}
                        </th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {sortedContacts.map((contact) => (
                        <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            <div className="flex items-center gap-x-3">
                              <Avatar firstName={contact.firstName} lastName={contact.lastName} size="sm" />
                              <span>{contact.firstName} {contact.lastName}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {contact.email || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {contact.phone || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {contact.title || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {(() => {
                              const owner = getUser(contact.ownerId)
                              if (!owner) return '-'
                              return (
                                <div className="flex items-center gap-x-2">
                                  <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                                  <span>{owner.firstName} {owner.lastName}</span>
                                </div>
                              )
                            })()}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/contacts/${contact.id}`}
                              className="inline-flex items-center gap-x-1 text-brand-600 hover:text-brand-900 mr-4"
                            >
                              <EyeIcon className="h-4 w-4" />
                              {t('common.view')}
                            </Link>
                            <button
                              onClick={() => deleteMutation.mutate(contact.id)}
                              className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {response && (
                    <Pagination
                      page={response.page}
                      totalPages={response.totalPages}
                      total={response.total}
                      limit={response.limit}
                      onPageChange={setPage}
                      onLimitChange={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
